/**
 * GoPay MoR G11 — 18+ acknowledgement on first selling page when required.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui/Button.js';
import {
  readAgeGateAcknowledged,
  requiresAgeGate,
  writeAgeGateAcknowledged,
} from './ageGateModel.js';

export interface AgeGateDialogProps {
  readonly tenantCode: string;
  readonly products: readonly { ageRestricted?: boolean | null }[];
  readonly reviewRisk: string | null | undefined;
  /**
   * When false, dialog stays hidden even if OR model would require the gate
   * (inputs still loading — callers must fail-closed on add/pay via certifyAgeGate).
   */
  readonly inputsResolved?: boolean;
  readonly testIdPrefix?: string;
  readonly onAcknowledged?: () => void;
}

export function AgeGateDialog({
  tenantCode,
  products,
  reviewRisk,
  inputsResolved = true,
  testIdPrefix = 'customer-age-gate',
  onAcknowledged,
}: AgeGateDialogProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const needed = inputsResolved && requiresAgeGate({ products, reviewRisk });
  const [dismissed, setDismissed] = useState(false);
  const code = tenantCode.trim();
  const alreadyAcked = code.length > 0 && readAgeGateAcknowledged(code);
  const open = needed && code.length > 0 && !alreadyAcked && !dismissed;

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[var(--z-modal,50)] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testIdPrefix}-title`}
      data-testid={testIdPrefix}
    >
      <div className="w-full max-w-md rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--vcl-elevation-popover)]">
        <h2
          id={`${testIdPrefix}-title`}
          className="m-0 text-lg font-semibold text-[var(--color-on-surface)]"
        >
          {t('morChrome.ageGate.title')}
        </h2>
        <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
          {t('morChrome.ageGate.body')}
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            intent="primary"
            size="lg"
            block
            type="button"
            data-testid={`${testIdPrefix}-confirm`}
            onClick={() => {
              writeAgeGateAcknowledged(code);
              setDismissed(true);
              onAcknowledged?.();
            }}
          >
            {t('morChrome.ageGate.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
